// @ts-nocheck
import { fixDate } from "@store/Framework/Common/Utilities/reportingUtils";
import { ExpendituresItemModel, InvoiceDetailsModel } from "@store/Framework/Services/Reporting/ReportingModels";
import { InvoiceSummaryData, ProductDataLineItems, Products } from "@store/Framework/Types/types";
import { DataTableData } from "@store/Library/Components/Reporting/ApiDataTable/ReportDataTable";
import {
    invoiceDetailHeaders,
    invoiceDetailKeys,
} from "@store/Library/Components/Reporting/InvoiceDetail/InvoiceDetailModal";
import {
    ExpenditureCompanies,
    ExpenditureLocationData,
    getOrderTotalFromExpenditureLocationData,
} from "@store/Library/Widgets/Reports/ExpenditureReports";
import {
    CompanyInvoiceSummaries,
    invoiceSummaryHeaders,
    invoiceSummaryKeys,
} from "@store/Library/Widgets/Reports/InvoiceDetailSearchForm";
import xlsx from "xlsx";

const getSummaryDataByInvoiceNumber = (invoiceSummaryData: InvoiceSummaryData[]) => {
    const summaryDataByInvoiceNum = invoiceSummaryData.reduce((acc, summary) => {
        const invoiceNum = summary.invoicenum;
        acc[invoiceNum] = summary;
        return acc;
    }, {} as { [invoiceNum: string]: InvoiceSummaryData });
    return summaryDataByInvoiceNum;
};

const getInvoiceDetailSheet = (
    invoiceDetails: InvoiceDetailsModel,
    summaryDataByInvoiceNum: { [invoiceNum: string]: InvoiceSummaryData },
    combinedSummaryKeys: (keyof InvoiceSummaryData)[],
    extendedHeaders: string[],
) => {
    const sheetArray: any[][] = [];
    sheetArray.push(extendedHeaders);
    Object.keys(summaryDataByInvoiceNum).forEach(invoiceNum => {
        const invoiceSummary = summaryDataByInvoiceNum[invoiceNum];
        const invoiceDetailData = invoiceDetails.invoices.find(invoice => invoice.invoiceNum === invoiceNum)?.items;
        if (!invoiceDetailData) {
            return;
        }
        const combinedSummary = combinedSummaryKeys.map(key => {
            return invoiceSummary[key];
        });
        const fixedCombinedSummary = combinedSummary.map(value => (value ? fixDate(value) : ""));
        const invoiceDetailRows = invoiceDetailData.map(invoiceDetail => {
            const invoiceDetailRow = invoiceDetailKeys.map(key => invoiceDetail[key]);
            return [...fixedCombinedSummary, ...invoiceDetailRow];
        });
        sheetArray.push(...invoiceDetailRows);
    });
    const ws = xlsx.utils.aoa_to_sheet(sheetArray);
    fixWidths(ws);
    return ws;
};

export const fixSheetName = (sheetName: string) => {
    const sheetNameWithoutSlashes = sheetName.replace("/", "-");
    return sheetNameWithoutSlashes.replace(/[\/\\?*:[]]/g, "").slice(0, 31);
};
const fixWidths = (worksheet: xlsx.WorkSheet) => {
    const widths = Array(20)
        .fill("")
        .map(() => {
            return { width: 20 };
        });
    worksheet["!cols"] = widths;
};

export const invoiceDetailAndSummaryExport = (
    invoiceSummaries: InvoiceSummaryData[] | undefined,
    invoiceDetailsData: InvoiceDetailsModel | undefined,
    companyInvoiceSummaries: CompanyInvoiceSummaries,
) => {
    const wb = xlsx.utils.book_new();
    if (!invoiceSummaries?.length) {
        return;
    }

    const combinedHeaders = invoiceSummaryHeaders.concat(invoiceDetailHeaders);
    const extendedHeaders = ["Customer #", "Customer", "Location ID", "Location"].concat(combinedHeaders);
    const extendedSummaryKeys: (keyof InvoiceSummaryData)[] = [
        "companyid",
        "companyname",
        "customerid",
        "customername",
    ];
    const combinedSummaryKeys = extendedSummaryKeys.concat(invoiceSummaryKeys);
    const summaryDataByInvoiceNum = getSummaryDataByInvoiceNumber(invoiceSummaries);

    const allDataWorkSheet = getInvoiceDetailSheet(
        invoiceDetailsData!,
        summaryDataByInvoiceNum,
        combinedSummaryKeys,
        extendedHeaders,
    );
    xlsx.utils.book_append_sheet(wb, allDataWorkSheet, "All Data");
    Object.values(companyInvoiceSummaries).forEach(company => {
        const companyName = company.name;
        const companyInvoiceSummary = company.locations;
        const combinedInvoiceSummaries = ([] as InvoiceSummaryData[]).concat(...Object.values(companyInvoiceSummary));
        const summaryDataByInvoiceNum = getSummaryDataByInvoiceNumber(combinedInvoiceSummaries);
        const companyWorkSheet = getInvoiceDetailSheet(
            invoiceDetailsData!,
            summaryDataByInvoiceNum,
            combinedSummaryKeys,
            extendedHeaders,
        );
        xlsx.utils.book_append_sheet(wb, companyWorkSheet, fixSheetName(companyName));
    });
    xlsx.writeFile(wb, "InvoiceDetails.xlsx");
};

export const createSheetFromProductData = (
    productData: Products,
    headers: string[],
    dataKeys: string[],
    dataSummaryTitle: string,
    title: string,
) => {
    const sheet1 = Object.keys(productData).reduce(
        (acc, itemName) => {
            const product = productData[itemName];

            const productTableData = {
                itemName,
                itemNumber: product.itemNumber,
                totCost: product.totCost,
                totQuantity: product.totQuantity,
            };
            acc.data.push(productTableData);
            return acc;
        },
        {
            headers: ["Item #", "Item Name", "Total Item Cost", "Total Item Quantity"],
            dataKeys: ["itemNumber", "itemName", "totCost", "totQuantity"],
            data: [],
            title: `${title}Total`,
            dataSummaryTitle,
        } as DataTableData<any>,
    );
    const sheet2 = Object.keys(productData).reduce(
        (acc, itemName) => {
            const product = productData[itemName];
            const productTableData = product.data.map(data => ({
                ...data,
                itemNumber: product?.itemNumber ?? data.itemNumber,
                itemName,
            }));
            acc.data.push(...productTableData);
            return acc;
        },
        {
            headers: ["Item #", "Item Name", ...headers],
            dataKeys: ["itemNumber", "itemName", ...dataKeys],
            data: [],
            title: `${title}Details`,
            dataSummaryTitle,
        } as DataTableData<any>,
    );

    return [sheet1, sheet2];
};

export const expenditureByManufacturerExport = (
    expenditureByManufacturersData: Products,
    headers: string[],
    dataKeys: (keyof ProductDataLineItems)[],
) => {
    const sheets = createSheetFromProductData(
        expenditureByManufacturersData,
        headers,
        dataKeys,
        "Expenditure By Manufacturer",
        "",
    );
    return sheets.map(sheet => ({
        ...sheet,
        headers: sheet.headers.slice(1),
        dataKeys: sheet.dataKeys.slice(1),
    }));
};

// adding this to add better type safety to the function
type ExpenditureLocationDataKeys = keyof ExpendituresItemModel | "locationName" | "totCost";

export const expenditureReportExport = (
    expenditureCompanies: ExpenditureCompanies,
    fullCostDataKeys: (keyof ExpenditureLocationData)[],
    fullCostHeaders: string[],
): DataTableData<any>[] => {
    const extendedHeaders = ["Company", "Location", "Total Location Cost", ...fullCostHeaders];
    const extendedDataKeys: ExpenditureLocationDataKeys[] = [
        "compname",
        "locationName",
        "totCost",
        ...fullCostDataKeys,
    ];

    return Object.values(expenditureCompanies).map(company => {
        const { name, locations } = company;
        const tableData: any[] = [];
        for (const items of Object.values(locations)) {
            for (const item of items) {
                tableData.push({
                    ...item,
                    name,
                    locationName: item.name,
                    totCost: getOrderTotalFromExpenditureLocationData(items),
                });
            }
        }
        return {
            headers: extendedHeaders,
            dataKeys: extendedDataKeys,
            data: tableData,
            title: name,
            dataSummaryTitle: "Expenditure Report",
        };
    });
};
